import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';

export const DemReconPerspective = () => {
  const entityPerspectiveRef = useRef();

  const views = [
    {
      name: 'SAP Reconiliction',
      dims: ['PAID_PERIOD', 'CHANNEL', 'PROD', 'EARNED_PERIOD'],
      kpis: ['SAP_AMT', 'PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT', 'DISCREPANCY_AMT', 'DELTA_AMT'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['LIFETIME', 'YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR'],
      },
    },
  ];

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'discrepancy',
      icon: fas.faExclamationTriangle,
      colors: [null, 'red', 'green'],
      toolTips: ['Discrepancies and no discrepancies', 'Discrepancies', 'No discrepancies'],
    },
    {
      key: 'adjusted',
      icon: fas.faPencil,
      colors: [null, 'green', 'grey'],
      toolTips: ['Adjusted and no adjustments', 'Adjusted', 'No adjustments'],
    },
  ]);

  const augButtons = (buttons) => {
    buttons.unshift(...filterButtons, <TitleButtonDivider key="filterButtonDivider" />);
  };

  const augReqParam = useCallback(
    (reqParam) => {
      // Discrepancy
      const discrepancyButtonState = filterButtonStages.discrepancy;
      if (discrepancyButtonState > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          discrepancyButtonState === 1 ? ExprUtils.whereParams('discrepancy', 1) : ExprUtils.isNot('discrepancy', 1),
        );
      }

      // Adjusted
      const adjustedButtonState = filterButtonStages.adjusted;
      if (adjustedButtonState > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          adjustedButtonState === 1 ? ExprUtils.whereParams('adjusted', 1) : ExprUtils.isNot('adjusted', 1),
        );
      }
    },
    [filterButtonStages],
  );

  const rowIconsFn = (data) => {
    let rowIcons = [];

    // Closed
    if (data.closed) {
      rowIcons.push(<RowIcon key="closed" icon={fas.faCheckCircle} color="green" toolTip="Period is closed" />);
    }

    // Discrepancy row icons
    if (data.discrepancy === 1) {
      rowIcons.push(<RowIcon key="discrepancy" icon={fas.faExclamationTriangle} color="red" toolTip="Discrepancy" />);
    } else if (data.discrepancy > 0) {
      rowIcons.push(
        <RowIcon
          key="containsDiscrepancy"
          icon={fas.faExclamationTriangle}
          color="orange"
          toolTip="Contains a discrepancy"
        />,
      );
    }

    // Adjusted row icons
    if (data.adjusted === 1) {
      rowIcons.push(<RowIcon key="adjusted" icon={fas.faPencil} color="90ee90" toolTip="Adjusted" />);
    } else if (data.adjusted > 0) {
      // Light green
      rowIcons.push(<RowIcon key="containsAdjustment" icon={fas.faPencil} color="green" toolTip="Partly adjusted" />);
    }

    return rowIcons;
  };

  const isPeriodClosed = (node) => {
    while (node.data.dim !== 'PAID_PERIOD') {
      node = node.parent;
    }
    return node.data.closed;
  };

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    const navigationItems = [
      {
        name: 'Navigate to ERP Transactions',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'SAP_AMT', 'ERP Transactions'),
      },
      {
        name: 'Navigate to RCM Transactions',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'KR_PYMT', 'RCM Transactions'),
      },
      {
        name: 'Pivot RCM Transactions',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'KR_PYMT', 'RCM Transactions'),
        icon: fas.faTableColumns,
      },
      {
        name: 'ERP Transactions and RCM Transactions Lookup',
        action: () =>
          entityPerspectiveRef.current.navigateToLookupViewer(
            params,
            'SAP_AMT',
            'ERP Transactions',
            'KR_PYMT',
            'RCM Transactions',
          ),
      },
    ];

    const adjustmentItems = [
      {
        name: 'Adjust Payment Amount',
        icon: fas.faPencil,
        action: () => entityPerspectiveRef.current.overwrite(params, 'PYMT_ADJ'),
      },
    ];

    contextMenus.push(
      {
        name: 'Close this Period',
        disabled: isPeriodClosed(params.node),
        action: () =>
          entityPerspectiveRef.current.openActionDialog(params, 'Close this Period', 'RECON_CLOSE_ACTION_PLUGIN'),
        icon: fas.faCheckCircle,
      },
      {
        name: 'Transactions',
        subMenu: navigationItems,
      },
      {
        name: 'Adjust',
        icon: fas.faPencil,
        subMenu: adjustmentItems,
      },
      {
        name: 'Navigate to Payment Adjustments',
        action: () => entityPerspectiveRef.current.navigateToPerspectiveAdj(params, 'PYMT_ADJ', 'Payment Adjustments'),
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      rowIconsFn={rowIconsFn}
      augButtons={augButtons}
      augReqParam={augReqParam}
      onViewReset={onViewReset}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
